import { MouseEvent as ReactMouseEvent, useMemo, useState } from 'react';
import { ChevronsDownUp, ChevronsUpDown, Eye, EyeOff, Plus } from 'lucide-react';
import pluralize from 'pluralize';
import CopyButton from 'src/shared/components/ui/icons/copy-button';
import WithAccessControl from 'src/shared/components/utils/WithAccessControl';
import PrimitiveEntityList from 'src/shared/components/entities/components/PrimitiveEntityList';
import TagNameBadge from 'src/shared/components/entities/components/TagNameBadge';
import {
  EntityType,
  extractPrimitiveChildren,
  filterEntities,
  getEntityType,
  getMissingsTags,
} from 'src/shared/lib/entities.utils';
import { ActualsMetaNer, ActualsUpdateType, Entities } from 'src/features/messages/types/message.types';
import { Tag as TypeOfTag } from 'src/shared/types/model.types';
import { cn } from 'src/shared/lib/utils';

type SuperSmartEntityTreeProps = {
  depth?: number;
  path?: string[];
  parentTag?: TypeOfTag;
  actuals: ActualsMetaNer;
  entities: Entities[string];
  tags: Map<string, TypeOfTag>;
  showMissingTags?: boolean;
  smallLayout?: boolean;
  onDelete?: (path: string[]) => void;
  onDuplicate?: (path: string[]) => void;
  onAddArrayGroup?: (path: string[], parentTag: TypeOfTag) => void;
  onAddMissingFields?: (path: string[], parentTag: TypeOfTag) => void;
  onEditSave?: (values: string[], path: string[], action: ActualsUpdateType) => void;
};

export default function SuperSmartEntityTree({
  actuals,
  entities,
  tags,
  depth = 0,
  path = [],
  parentTag,
  showMissingTags = true,
  smallLayout = false,
  onDelete,
  onEditSave,
  onDuplicate,
  onAddArrayGroup,
  onAddMissingFields,
}: SuperSmartEntityTreeProps) {
  const [collapsedKeys, setCollapsedKeys] = useState<string[]>([]);
  const [collapsedObjects, setCollapsedObjects] = useState<string[]>([]);
  const [hiddenEmptyTagsKeys, setHiddenEmptyTagsKeys] = useState<string[]>([]);

  const nested: Entities[string] = {};
  const primitive: Record<string, string[] | null> = {};

  Object.entries(entities || {}).forEach(([key, val]) => {
    const tag = tags?.get(key);
    if (!tag) {
      console.warn('Tag not found:', key);
      return;
    }

    const entityType = getEntityType(val, tag);

    if (entityType === 'PRIMITIVE') {
      // Handle primitives: normalize scalar strings to arrays and re-route mis-classified objects.
      if (val === null) {
        primitive[key] = null;
      } else if (Array.isArray(val) && val.every((item) => typeof item === 'string' || item === null)) {
        // This is a valid primitive string array.
        primitive[key] = val;
      } else if (typeof val === 'string') {
        // This is a scalar string, normalize it to an array.
        primitive[key] = [val];
      } else {
        // The value is not a valid primitive type (e.g., an object, or an array of objects).
        // This is a mis-classification. Treat it as a nested entity to prevent a crash.
        console.warn('MIS-CLASSIFIED leaf tag:', tag.internalId, val);
        nested[key] = val;
      }
    } else {
      nested[key] = val;
    }
  });

  function hasMissingTags(
    nestedEntities: Entities[string][] | Entities[string],
    entityType: EntityType,
    parentTag: TypeOfTag
  ) {
    const primitiveChildren = extractPrimitiveChildren(Array.from(tags.values()), parentTag);

    const entitiesToCheck = nestedEntities ? (entityType === 'ARRAY' ? nestedEntities : [nestedEntities]) : [];

    if (!entitiesToCheck?.length) {
      return false;
    }

    for (const entity of entitiesToCheck as Entities[string][]) {
      const missingTags = getMissingsTags(entity, primitiveChildren);
      if (missingTags.length > 0) return true;
    }

    return false;
  }

  function handleToggleExpand(
    key: string,
    isCollapsedArray: boolean,
    isCollapsedObject: boolean,
    entityType: EntityType
  ) {
    if (entityType === 'OBJECT') {
      setCollapsedObjects((prev) => (isCollapsedObject ? prev.filter((k) => k !== key) : [...prev, key]));
    } else {
      setCollapsedKeys((prev) => (isCollapsedArray ? prev.filter((k) => k !== key) : [...prev, key]));
    }
  }

  function handleToggleShowMissingTagsOfChildren(
    e: ReactMouseEvent<HTMLButtonElement, MouseEvent>,
    isHidden: boolean,
    key: string
  ) {
    e.stopPropagation();
    setHiddenEmptyTagsKeys((prev) => (isHidden ? prev.filter((k) => k !== key) : [...prev, key]));
  }

  const primitiveChildren = useMemo(() => {
    return extractPrimitiveChildren(Array.from(tags.values()), parentTag);
  }, [parentTag, tags]);

  return (
    <div className="flex flex-col gap-1">
      {/* Render Primitives first */}
      {Object.keys(primitive).length > 0 && primitiveChildren.length > 0 && (
        <div className={cn('bg-white rounded-sm', depth === 0 && 'border px-1 pt-[3px] pb-[3px]')}>
          <div className="min-h-0 pt-0.5 px-1">
            <PrimitiveEntityList
              path={path}
              tags={primitiveChildren}
              actuals={actuals}
              entities={primitive}
              parentTag={parentTag}
              onDelete={onDelete}
              onEditSave={onEditSave}
              onDuplicate={onDuplicate}
              showEmptyTags={onEditSave && showMissingTags}
              smallLayout={smallLayout}
            />
          </div>
        </div>
      )}
      {/* Render Nested Entity arrays and objects */}
      {Object.keys(nested).length > 0 &&
        Object.entries(nested)
          .filter(([_, nestedEntities]) => onEditSave || filterEntities(nestedEntities))
          .map(([key, nestedEntities]) => {
            const tagConfig = tags?.get(key);
            if (!tagConfig) {
              return null;
            }
            const isCollapsedArray = collapsedKeys.includes(key);
            const isCollapsedObject = collapsedObjects.includes(key);
            const isCollapsed = isCollapsedArray || isCollapsedObject;

            const isHidden = hiddenEmptyTagsKeys.includes(key);

            const entityType = getEntityType(nestedEntities, tagConfig);

            const missesTags = hasMissingTags(
              nestedEntities as Entities[string][] | Entities[string],
              entityType,
              tagConfig
            );

            const icon = entityType === 'ARRAY' ? 'List' : entityType === 'OBJECT' ? 'Braces' : 'Tag';

            // find missing children for object entities
            const missingObjectChildren: TypeOfTag[] = [];
            if (entityType === 'OBJECT') {
              for (const child of tagConfig.children || []) {
                const childTag = tags?.get(child.id);
                if (childTag && !Object.keys(nestedEntities || {}).includes(childTag.internalId)) {
                  missingObjectChildren.push(childTag);
                }
              }
            }

            return (
              <div key={key} className={cn('border bg-white rounded-sm p-1')}>
                <div className="flex items-center justify-between gap-1">
                  <TagNameBadge
                    isPrimitive={icon === 'Tag'}
                    className="max-w-full w-full cursor-text hover:bg-white"
                    tagName={
                      (tagConfig.shortName || key) +
                      (entityType === 'ARRAY'
                        ? ' (' + ((nestedEntities as Array<Entities[string]>)?.filter(Boolean).length || 0) + ')'
                        : '')
                    }
                  >
                    <div className="flex items-center gap-1">
                      {onEditSave && !isCollapsed && smallLayout && (
                        <button
                          className="shrink-0 select-none opacity-50 hover:opacity-100 text-xs flex-center px-1"
                          onClick={(e) => handleToggleShowMissingTagsOfChildren(e, isHidden, key)}
                        >
                          {missesTags ? isHidden ? <Eye size={12} /> : <EyeOff size={12} /> : null}
                        </button>
                      )}
                      <button
                        className="shrink-0 select-none opacity-50 hover:opacity-100 text-xs flex-center gap-1 px-1"
                        onClick={() => handleToggleExpand(key, isCollapsedArray, isCollapsedObject, entityType)}
                      >
                        {isCollapsed ? <ChevronsUpDown size={12} /> : <ChevronsDownUp size={12} />}
                      </button>
                      <WithAccessControl>
                        <CopyButton
                          className="px-1"
                          variant="subtle"
                          value={JSON.stringify(nestedEntities || {}, null, 4)}
                        />
                      </WithAccessControl>
                    </div>
                  </TagNameBadge>
                </div>

                {entityType === 'ARRAY' ? (
                  isCollapsedArray ? null : (
                    <div className="flex flex-col gap-1 max-w-full mt-1">
                      {(nestedEntities as Entities[string][])?.map((entity, index) => {
                        if (!entity) {
                          return null;
                        }
                        return (
                          <div key={index} className="border p-1 rounded-sm hover:border-neutral-500">
                            <SuperSmartEntityTree
                              tags={tags}
                              depth={depth + 1}
                              actuals={actuals}
                              entities={entity}
                              parentTag={tagConfig}
                              path={path.concat(key, index.toString())}
                              showMissingTags={!isHidden}
                              smallLayout={smallLayout}
                              onDelete={onDelete}
                              onEditSave={onEditSave}
                              onDuplicate={onDuplicate}
                              onAddArrayGroup={onAddArrayGroup}
                              onAddMissingFields={onAddMissingFields}
                            />
                          </div>
                        );
                      })}
                      {onAddArrayGroup && (
                        <button
                          onClick={() => onAddArrayGroup(path, tagConfig)}
                          className="hover:bg-primary hover:text-white hover:border-solid hover:border-primary transition-all text-primary-500 px-2 py-1 rounded-sm border border-dashed flex items-center gap-1 text-sm text-typography-grey"
                        >
                          <Plus size={16} /> Add {tagConfig.shortName}
                        </button>
                      )}
                    </div>
                  )
                ) : isCollapsedObject ? null : (
                  <div className="flex flex-col gap-1 mt-1">
                    <SuperSmartEntityTree
                      tags={tags}
                      depth={depth + 1}
                      actuals={actuals}
                      parentTag={tagConfig}
                      path={path.concat(key)}
                      entities={nestedEntities as Entities[string]}
                      showMissingTags={!isHidden}
                      smallLayout={smallLayout}
                      onDelete={onDelete}
                      onEditSave={onEditSave}
                      onDuplicate={onDuplicate}
                      onAddArrayGroup={onAddArrayGroup}
                      onAddMissingFields={onAddMissingFields}
                    />

                    {onAddMissingFields && missingObjectChildren.length > 0 && (
                      <button
                        onClick={() => onAddMissingFields(path, tagConfig)}
                        className="w-full hover:bg-primary hover:text-white hover:border-solid hover:border-primary transition-all text-primary-500 px-2 py-1 rounded-sm border border-dashed flex items-center gap-1 text-sm text-typography-grey"
                      >
                        <Plus size={16} />
                        Add {missingObjectChildren.length} missing {pluralize('field', missingObjectChildren.length)} to{' '}
                        {tagConfig.shortName}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
    </div>
  );
}
